import React, { useState } from "react";
import { readAffiliateTotalCommission } from "../utils/affiliateCommission";

// -- Tooltip component for animated tooltips --
function Tooltip({ children, label }) {
  return (
    <div className="relative group">
      {children}
      <span className="absolute z-10 left-1/2 -translate-x-1/2 top/full mt-2 w-max bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap pointer-events-none">
        {label}
      </span>
    </div>
  );
}

// --- price helpers ---
function getPriceNumber(item) {
  const candidates = [item?.price, item?.basePrice, item?.amount];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function formatPrice(item) {
  const n = getPriceNumber(item);
  if (!Number.isFinite(n) || n <= 0) return "Free";
  return `LKR ${n.toLocaleString("en-LK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatCommission(item) {
  if (!item?.affiliateEnabled) return "Off";
  const n = readAffiliateTotalCommission(item);
  return `LKR ${n.toLocaleString("en-LK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function isProductItem(item = {}) {
  return String(item?.type || "").toLowerCase() === "product";
}

function isListingActive(item = {}) {
  return item?.isActive !== false;
}

function formatInventory(item = {}) {
  const raw = item?.stock ?? item?.inventory;
  if (raw == null || raw === "") return "—";
  const value = Number(raw);
  return Number.isFinite(value) ? value.toLocaleString("en-LK") : "—";
}

/* ---------------- Drive helpers (for image thumbnails) ---------------- */

function extractDriveId(rawUrl = "") {
  const url = String(rawUrl || "").trim();
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("drive.google.com")) return null;
    // /file/d/<id>/view
    const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (m && m[1]) return m[1];
    // ?id=<id>
    const q = u.searchParams.get("id");
    if (q) return q;
  } catch (_) {}
  return null;
}

// a few reliable public endpoints; we’ll try them in order
function buildDriveImageCandidates(thumbnailUrl) {
  const id = extractDriveId(thumbnailUrl);
  if (!id) return [];
  return [
    `https://drive.usercontent.google.com/uc?id=${id}&export=view`,
    `https://drive.google.com/uc?export=view&id=${id}`,
    `https://lh3.googleusercontent.com/d/${id}=w800-h800-no`,
    `https://drive.google.com/thumbnail?id=${id}&sz=w800`,
  ];
}

/* ------------------ Small image chain (no style changes) ------------------ */
function SmartThumb({ item, className = "w-12 h-12 rounded-lg object-cover" }) {
  const youTubeThumb =
    item?.youtubeLinks && item.youtubeLinks[0]
      ? item.youtubeLinks[0].thumbnail ||
        getYouTubeThumbnail(item.youtubeLinks[0].url)
      : null;

  const candidates = [];
  if (item?.thumbnailUrl) {
    const driveCandidates = buildDriveImageCandidates(item.thumbnailUrl);
    if (driveCandidates.length) candidates.push(...driveCandidates);
    else candidates.push(item.thumbnailUrl);
  }
  if (Array.isArray(item?.productImages)) {
    item.productImages.forEach((img) => {
      const raw = typeof img === "string" ? img : img?.url;
      if (!raw) return;
      const driveCandidates = buildDriveImageCandidates(raw);
      if (driveCandidates.length) candidates.push(...driveCandidates);
      else candidates.push(raw);
    });
  }
  if (youTubeThumb) candidates.push(youTubeThumb);
  candidates.push("/placeholder.png"); // final fallback

  const [idx, setIdx] = useState(0);
  const src = candidates[Math.min(idx, candidates.length - 1)];

  return (
    <img
      src={src}
      alt="Thumbnail"
      className={className}
      referrerPolicy="no-referrer"
      onError={() => {
        if (idx < candidates.length - 1) setIdx((n) => n + 1);
      }}
      draggable="false"
    />
  );
}

// -- Details drawer for quick view/edit --
function CourseDetailsDrawer({ open, onClose, course }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="bg-white w-full max-w-md h-full shadow-xl px-8 py-8 overflow-y-auto relative">
        <button
          className="absolute top-4 right-6 text-2xl text-gray-400 hover:text-gray-700"
          onClick={onClose}
        >
          ×
        </button>
        <h2 className="text-2xl font-bold mb-4">{course?.title}</h2>

        {/* use the same smart Drive-aware thumb here */}
        <SmartThumb
          item={{
            thumbnailUrl: course?.thumbnailUrl || course?.thumbnail,
            youtubeLinks: course?.youtubeLinks || [],
          }}
          className="w-32 h-32 rounded-lg object-cover shadow mb-4"
        />

        <div className="mb-2">
          <b>Type:</b> {course?.type}
        </div>
        <div className="mb-2">
          <b>Status:</b> {course?.status}
        </div>
        <div className="mb-2">
          <b>Sales:</b> {course?.sales}
        </div>
        <div className="mb-2">
          <b>Description:</b> {course?.description || "No description"}
        </div>
        <div className="mb-2">
          <b>Affiliate:</b> {course?.affiliateEnabled ? "Enabled" : "Disabled"}
        </div>
        <div className="mb-2">
          <b>Commission:</b> {formatCommission(course)}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status, onClick, selected }) {
  const colors = {
    approved: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-700",
    declined: "bg-red-100 text-red-700",
    all: "bg-gray-200 text-gray-800",
  };
  return (
    <button
      className={`px-3 py-1 rounded-full text-xs font-semibold mr-2 border transition
        ${colors[status] || colors.all} ${
          selected ? "border-blue-400 shadow" : "border-transparent"
        }`}
      onClick={onClick}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </button>
  );
}

function getYouTubeThumbnail(url) {
  const match = String(url || "").match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/,
  );
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
}

export default function CourseProductTable({
  data = [],
  onEdit,
  onAnalytics,
  onDelete,
  onToggleActive,
}) {
  // Example data fallback
  data = data.length ? data : [];

  console.log("data", data);

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const totalPages = Math.ceil(data.length / pageSize);

  // Search & filter
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Bulk select
  const [selectedRows, setSelectedRows] = useState([]);

  // Side drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCourse, setDrawerCourse] = useState(null);

  // Filter & search
  let filtered = data;
  if (statusFilter !== "all")
    filtered = filtered.filter((d) => d.status === statusFilter);
  if (search)
    filtered = filtered.filter((d) =>
      d.title.toLowerCase().includes(search.toLowerCase()),
    );

  // Paginated data
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Status badge
  const statusBadge = (status) => {
    if (status === "approved")
      return (
        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
          Approved
        </span>
      );
    if (status === "pending")
      return (
        <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-semibold">
          Pending
        </span>
      );
    return (
      <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">
        Declined
      </span>
    );
  };

  // Select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(paged.map((d) => d.id));
    } else {
      setSelectedRows([]);
    }
  };

  // Select row
  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rid) => rid !== id) : [...prev, id],
    );
  };

  return (
    <div>
      {/* Filters and Search */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <input
          className="px-3 py-2 border rounded-xl focus:outline-none focus:ring w-60"
          placeholder="Search by title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <StatusChip
          status="all"
          selected={statusFilter === "all"}
          onClick={() => setStatusFilter("all")}
        />
        <StatusChip
          status="approved"
          selected={statusFilter === "approved"}
          onClick={() => setStatusFilter("approved")}
        />
        <StatusChip
          status="pending"
          selected={statusFilter === "pending"}
          onClick={() => setStatusFilter("pending")}
        />
        <StatusChip
          status="declined"
          selected={statusFilter === "declined"}
          onClick={() => setStatusFilter("declined")}
        />
        {selectedRows.length > 0 && (
          <span className="ml-auto text-blue-600 font-bold">
            {selectedRows.length} selected
            <button
              className="ml-3 px-3 py-1 rounded bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 transition"
              onClick={() => alert("Bulk delete feature!")}
            >
              Delete Selected
            </button>
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl shadow-lg">
        <table className="min-w-full text-sm bg-white">
          {/* <thead>
            <tr className="text-left text-gray-500 bg-gray-50">
              <th className="p-3">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={paged.every((d) => selectedRows.includes(d.id))}
                />
              </th>
              <th className="p-3">#</th>
              <th className="p-3">Thumbnail</th>
              <th className="p-3">Title</th>
              <th className="p-3">Type</th>
              <th className="p-3">Status</th>
              <th className="p-3">Sales</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead> */}
          <thead>
            <tr className="text-left text-gray-500 bg-gray-50">
              <th className="p-3">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={paged.every((d) => selectedRows.includes(d.id))}
                />
              </th>
              <th className="p-3">#</th>
              <th className="p-3">Thumbnail</th>
              <th className="p-3">Title</th>
              <th className="p-3">Type</th>
              <th className="p-3">Status</th>
              <th className="p-3">Affiliate</th>
              <th className="p-3 text-right">Price</th>
              <th className="p-3 text-right">Stock</th>
              <th className="p-3 text-right">Commission</th>
              <th className="p-3">Storefront</th>
              <th className="p-3">Sales</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {paged.map((item, i) => (
              <tr
                key={item.id}
                className={`border-b last:border-none group transition
                  hover:bg-blue-50 hover:shadow-lg cursor-pointer`}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(item.id)}
                    onChange={() => handleSelectRow(item.id)}
                  />
                </td>
                <td className="p-3 text-gray-400">
                  {(page - 1) * pageSize + i + 1}
                </td>

                <td className="p-3">
                  {/* Drive-aware image (no style change) */}
                  <SmartThumb
                    item={item}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                </td>

                <td
                  className="p-3 font-medium text-blue-700 hover:underline"
                  onClick={() => {
                    setDrawerCourse(item);
                    setDrawerOpen(true);
                  }}
                >
                  {item.title}
                </td>
                <td className="p-3">
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                    {item.type}
                  </span>
                </td>
                <td className="p-3">{statusBadge(item.status)}</td>
                <td className="p-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      item.affiliateEnabled
                        ? "bg-fuchsia-100 text-fuchsia-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {item.affiliateEnabled ? "Enabled" : "Off"}
                  </span>
                </td>
                <td className="p-3 text-right">{formatPrice(item)}</td>
                <td className="p-3 text-right">{formatInventory(item)}</td>
                <td className="p-3 text-right">{formatCommission(item)}</td>
                <td className="p-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      isProductItem(item)
                        ? isListingActive(item)
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-200 text-slate-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {isProductItem(item)
                      ? isListingActive(item)
                        ? "Active"
                        : "Inactive"
                      : "Live"}
                  </span>
                </td>
                <td className="p-3">{item.sales}</td>
                <td className="p-3 flex gap-3">
                  <Tooltip label="Edit">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit && onEdit(item);
                      }}
                      className="p-1 hover:bg-blue-100 rounded"
                    >
                      <svg
                        width={18}
                        height={18}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path d="M15.232 5.232l-2.464-2.464a2 2 0 00-2.828 0l-5.536 5.536a2 2 0 000 2.828l2.464 2.464a2 2 0 002.828 0l5.536-5.536a2 2 0 000-2.828z" />
                      </svg>
                    </button>
                  </Tooltip>
                  <Tooltip label="Delete">
                    <button
                      className="p-1 hover:bg-red-100 rounded"
                      onClick={() => onDelete(item)}
                    >
                      <svg
                        width={18}
                        height={18}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path d="M6 6v6m6-6v6M5 6h8M5 6V5a2 2 0 012-2h4a2 2 0 012 2v1m-8 0v7a2 2 0 002 2h4a2 2 0 002-2V6" />
                      </svg>
                    </button>
                  </Tooltip>
                  {isProductItem(item) && onToggleActive ? (
                    <Tooltip
                      label={isListingActive(item) ? "Deactivate" : "Activate"}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleActive(item);
                        }}
                        className="p-1 hover:bg-amber-100 rounded"
                      >
                        <svg
                          width={18}
                          height={18}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path d="M9 2v7" />
                          <path d="M4.5 5.5a7 7 0 109 0" />
                        </svg>
                      </button>
                    </Tooltip>
                  ) : null}
                  <Tooltip label="Analytics">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAnalytics && onAnalytics(item);
                      }}
                      className="p-1 hover:bg-green-100 rounded"
                    >
                      <svg
                        width={18}
                        height={18}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path d="M12 20V10M18 20V4M6 20v-4" />
                      </svg>
                    </button>
                  </Tooltip>
                </td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr>
                <td colSpan={13} className="text-center py-8 text-gray-400">
                  No results found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <button
            className="px-3 py-1 rounded bg-gray-50 text-gray-600 font-semibold hover:bg-gray-100"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Prev
          </button>
          <span>
            Page <b>{page}</b> of <b>{totalPages}</b>
          </span>
          <button
            className="px-3 py-1 rounded bg-gray-50 text-gray-600 font-semibold hover:bg-gray-100"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Details Drawer */}
      <CourseDetailsDrawer
        open={drawerOpen}
        course={drawerCourse}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
